import { get, post } from '../api';
import { state } from '../state';
import { showToast, errToast } from '../components/toast';

interface AiqApp {
  id: string;
  name: string;
  status: string;
  icon: string;
}

interface AiqTag {
  id: string;
  name: string;
  color: string;
  saved: number;
}

interface AiqThread {
  id: string;
  app_id: string;
  title: string;
  summary: string;
  author_id: string;
  status: string;
  resolved_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

interface AiqMessage {
  id: string;
  thread_id: string;
  author_id: string;
  content: string;
  created_at: string;
}

interface ThreadDetail extends AiqThread {
  tags: AiqTag[];
  messages: AiqMessage[];
}

let currentView: 'apps' | 'threads' | 'thread' = 'apps';
let currentAppId = '';

export function cleanupAiQuestions(): void {
  currentView = 'apps';
  currentAppId = '';
}

export async function renderAiQuestions(container: HTMLElement, channel: string): Promise<void> {
  container.dataset.rendererKind = 'ai_questions';
  injectStyles();

  const sid = state.currentServer ?? '';
  if (!sid) { container.innerHTML = '<div class="aiq-empty">No server selected.</div>'; return; }

  container.innerHTML = '<div class="aiq-wrap" id="aiq-wrap"></div>';
  const wrap = container.querySelector('#aiq-wrap') as HTMLElement;

  await showApps(wrap, sid, channel);
}

async function showApps(wrap: HTMLElement, sid: string, channel: string): Promise<void> {
  currentView = 'apps';
  currentAppId = '';

  let apps: AiqApp[] = [];
  try {
    apps = await get<AiqApp[]>(`/api/ai-questions/apps?server_id=${enc(sid)}&channel=${enc(channel)}`);
  } catch {
    wrap.innerHTML = '<div class="aiq-empty">Failed to load apps.</div>';
    return;
  }

  const cards = apps.map(a => {
    const isLive = a.status === 'live';
    const icon = a.icon || a.name.charAt(0).toUpperCase();
    const delBtn = isLive ? '' : `<button class="aiq-card-del" data-del="${esc(a.id)}" title="Delete" aria-label="Delete">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    </button>`;
    return `<button class="aiq-card${isLive ? ' aiq-card-live' : ''}" data-app="${esc(a.id)}" type="button">
      <div class="aiq-card-icon">${esc(icon)}</div>
      <div class="aiq-card-name">${esc(a.name)}</div>
      <div class="aiq-card-status">${isLive ? 'Live' : 'Idea'}</div>
      ${delBtn}
    </button>`;
  }).join('');

  wrap.innerHTML = `
    <div class="aiq-header">
      <span class="aiq-header-title">${channel === 'cc-questions' ? 'CC Questions' : 'My Questions'}</span>
    </div>
    <div class="aiq-grid">
      ${cards}
      <button class="aiq-card aiq-card-create" id="aiq-create-app" type="button">
        <div class="aiq-card-icon aiq-card-icon-add">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </div>
        <div class="aiq-card-name">New App Idea</div>
      </button>
    </div>
  `;

  wrap.querySelectorAll<HTMLElement>('.aiq-card[data-app]').forEach(card => {
    card.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.aiq-card-del')) return;
      const appId = card.dataset.app ?? '';
      if (appId) void showThreads(wrap, sid, channel, appId, apps.find(a => a.id === appId)?.name ?? '');
    });
  });

  wrap.querySelectorAll<HTMLElement>('.aiq-card-del').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = btn.dataset.del ?? '';
      if (!id) return;
      try {
        await post(`/api/ai-questions/apps/${enc(id)}`, {});
        showToast('App idea deleted', 'ok');
        void showApps(wrap, sid, channel);
      } catch (err) { errToast(err); }
    });
  });

  document.getElementById('aiq-create-app')?.addEventListener('click', () => {
    showCreateAppModal(wrap, sid, channel);
  });
}

function showCreateAppModal(wrap: HTMLElement, sid: string, channel: string): void {
  const existing = document.querySelector('.aiq-modal-backdrop');
  if (existing) existing.remove();

  const backdrop = document.createElement('div');
  backdrop.className = 'aiq-modal-backdrop';
  backdrop.innerHTML = `<div class="aiq-modal">
    <div class="aiq-modal-title">New App Idea</div>
    <input class="aiq-modal-input" id="aiq-app-name" placeholder="App name" maxlength="64" autocomplete="off" />
    <div class="aiq-modal-actions">
      <button class="aiq-btn aiq-btn-secondary" id="aiq-app-cancel">Cancel</button>
      <button class="aiq-btn aiq-btn-primary" id="aiq-app-save">Create</button>
    </div>
  </div>`;
  document.body.appendChild(backdrop);

  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) backdrop.remove(); });
  document.getElementById('aiq-app-cancel')?.addEventListener('click', () => backdrop.remove());
  document.getElementById('aiq-app-save')?.addEventListener('click', async () => {
    const name = (document.getElementById('aiq-app-name') as HTMLInputElement).value.trim();
    if (!name) { showToast('Enter a name', 'err'); return; }
    try {
      await post('/api/ai-questions/apps/create', { server_id: sid, channel, name });
      backdrop.remove();
      showToast('App idea created', 'ok');
      void showApps(wrap, sid, channel);
    } catch (err) { errToast(err); }
  });

  (document.getElementById('aiq-app-name') as HTMLInputElement)?.focus();
}

async function showThreads(
  wrap: HTMLElement, sid: string, channel: string, appId: string, appName: string,
): Promise<void> {
  currentView = 'threads';
  currentAppId = appId;

  let threads: AiqThread[] = [];
  let savedTags: AiqTag[] = [];
  try {
    [threads, savedTags] = await Promise.all([
      get<AiqThread[]>(`/api/ai-questions/threads?server_id=${enc(sid)}&channel=${enc(channel)}&app_id=${enc(appId)}`),
      get<AiqTag[]>(`/api/ai-questions/tags?server_id=${enc(sid)}`),
    ]);
  } catch {
    wrap.innerHTML = '<div class="aiq-empty">Failed to load.</div>';
    return;
  }

  const threadCards = threads.map(t => {
    const isResolved = t.status === 'resolved';
    return `<div class="aiq-thread-card${isResolved ? ' aiq-thread-resolved' : ''}" data-thread="${esc(t.id)}">
      <div class="aiq-thread-top">
        <span class="aiq-thread-title">${esc(t.title)}</span>
        ${isResolved ? '<span class="aiq-thread-badge-resolved">Resolved</span>' : '<span class="aiq-thread-badge-open">Open</span>'}
      </div>
      <div class="aiq-thread-summary">${esc(t.summary || 'No summary.')}</div>
      <div class="aiq-thread-meta">${fmtDate(t.updated_at)}</div>
    </div>`;
  }).join('') || '<div class="aiq-empty-small">No questions yet. Start one below.</div>';

  wrap.innerHTML = `
    <div class="aiq-header">
      <button class="aiq-back" id="aiq-back" type="button">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
      </button>
      <span class="aiq-header-title">${esc(appName)}</span>
    </div>
    <div class="aiq-threads-list">${threadCards}</div>
    <div class="aiq-create-thread">
      <input class="aiq-thread-input" id="aiq-thread-title" placeholder="What's blocked? (title)" maxlength="200" />
      <textarea class="aiq-thread-textarea" id="aiq-thread-summary" placeholder="What's the question or idea?" rows="3" maxlength="2000"></textarea>
      <button class="aiq-btn aiq-btn-primary" id="aiq-thread-create" type="button">Post Question</button>
    </div>
  `;

  document.getElementById('aiq-back')?.addEventListener('click', () => void showApps(wrap, sid, channel));

  wrap.querySelectorAll<HTMLElement>('.aiq-thread-card').forEach(card => {
    card.addEventListener('click', () => {
      const threadId = card.dataset.thread ?? '';
      if (threadId) void showThread(wrap, sid, channel, appId, appName, threadId);
    });
  });

  document.getElementById('aiq-thread-create')?.addEventListener('click', async () => {
    const title = (document.getElementById('aiq-thread-title') as HTMLInputElement).value.trim();
    const summary = (document.getElementById('aiq-thread-summary') as HTMLTextAreaElement).value.trim();
    if (!title) { showToast('Enter a title', 'err'); return; }
    try {
      await post('/api/ai-questions/threads/create', {
        server_id: sid, channel, app_id: appId, title, summary,
        author_id: state.ownerId || '',
      });
      showToast('Question posted', 'ok');
      void showThreads(wrap, sid, channel, appId, appName);
    } catch (err) { errToast(err); }
  });

  wrap.querySelectorAll<HTMLElement>('.aiq-thread-input, .aiq-thread-textarea').forEach(el => {
    el.addEventListener('focus', () => {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400);
    });
  });
}

async function showThread(
  wrap: HTMLElement, sid: string, channel: string, appId: string, appName: string, threadId: string,
): Promise<void> {
  currentView = 'thread';

  let detail: ThreadDetail;
  try {
    detail = await get<ThreadDetail>(`/api/ai-questions/threads/${enc(threadId)}`);
  } catch {
    wrap.innerHTML = '<div class="aiq-empty">Failed to load thread.</div>';
    return;
  }

  const isResolved = detail.status === 'resolved';
  const isArchived = !!detail.archived_at;
  const tagPills = (detail.tags || []).map(t =>
    `<span class="aiq-tag" style="background:${esc(t.color)}20;color:${esc(t.color)};border-color:${esc(t.color)}40">${esc(t.name)}</span>`
  ).join('');

  const msgs = (detail.messages || []).map(m => {
    const name = personName(m.author_id);
    const initial = (name || '?').charAt(0).toUpperCase();
    return `<div class="aiq-msg">
      <div class="aiq-msg-avatar">${esc(initial)}</div>
      <div class="aiq-msg-body">
        <div class="aiq-msg-meta">
          <span class="aiq-msg-author">${esc(name)}</span>
          <span class="aiq-msg-time">${fmtTime(m.created_at)}</span>
        </div>
        <div class="aiq-msg-content">${esc(m.content)}</div>
      </div>
    </div>`;
  }).join('') || '<div class="aiq-empty-small">No replies yet.</div>';

  const actionBtns = isArchived ? '<div class="aiq-thread-archived-label">Archived</div>'
    : isResolved
      ? `<button class="aiq-btn aiq-btn-secondary" id="aiq-archive" type="button">Archive</button>`
      : `<button class="aiq-btn aiq-btn-primary" id="aiq-resolve" type="button">Mark Resolved</button>`;

  const composeHtml = isResolved ? '' : `
    <div class="aiq-compose">
      <textarea class="aiq-compose-input" id="aiq-reply-input" placeholder="Reply…  (Enter to send · Shift+Enter = new line)" rows="2"></textarea>
      <button class="aiq-btn aiq-btn-primary aiq-compose-send" id="aiq-reply-send" type="button">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
      </button>
    </div>`;

  wrap.innerHTML = `
    <div class="aiq-header">
      <button class="aiq-back" id="aiq-back-thread" type="button">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
      </button>
      <span class="aiq-header-title">${esc(detail.title)}</span>
      <div class="aiq-header-actions">${actionBtns}</div>
    </div>
    ${tagPills ? `<div class="aiq-tag-row">${tagPills}</div>` : ''}
    ${detail.summary ? `<div class="aiq-thread-summary-block">${esc(detail.summary)}</div>` : ''}
    <div class="aiq-msgs" id="aiq-msgs">${msgs}</div>
    ${composeHtml}
  `;

  document.getElementById('aiq-back-thread')?.addEventListener('click', () =>
    void showThreads(wrap, sid, channel, appId, appName)
  );

  document.getElementById('aiq-resolve')?.addEventListener('click', async () => {
    try {
      await post(`/api/ai-questions/threads/${enc(threadId)}/resolve`, { person_id: state.ownerId || '' });
      showToast('Thread resolved', 'ok');
      void showThread(wrap, sid, channel, appId, appName, threadId);
    } catch (err) { errToast(err); }
  });

  document.getElementById('aiq-archive')?.addEventListener('click', async () => {
    try {
      await post(`/api/ai-questions/threads/${enc(threadId)}/archive`, {});
      showToast('Thread archived', 'ok');
      void showThreads(wrap, sid, channel, appId, appName);
    } catch (err) { errToast(err); }
  });

  const replyInput = document.getElementById('aiq-reply-input') as HTMLTextAreaElement | null;
  const sendReply = async (): Promise<void> => {
    const text = replyInput?.value.trim();
    if (!text) return;
    try {
      await post('/api/ai-questions/messages', {
        thread_id: threadId,
        author_id: state.ownerId || '',
        content: text,
      });
      if (replyInput) replyInput.value = '';
      void showThread(wrap, sid, channel, appId, appName, threadId);
    } catch (err) { errToast(err); }
  };
  document.getElementById('aiq-reply-send')?.addEventListener('click', () => void sendReply());
  // Enter-to-send (Shift+Enter = new line) — default across the app, incl. mobile.
  replyInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      void sendReply();
    }
  });

  replyInput?.addEventListener('focus', () => {
    setTimeout(() => replyInput.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  });

  const msgsEl = document.getElementById('aiq-msgs');
  if (msgsEl) msgsEl.scrollTop = msgsEl.scrollHeight;
}

function personName(id: string): string {
  const p = state.people.find(x => x.id === id);
  return p?.name ?? (id === state.ownerId ? 'You' : id.slice(0, 8));
}
function fmtDate(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`;
}
function esc(s: string): string { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
function enc(s: string): string { return encodeURIComponent(s); }

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .aiq-wrap { display:flex; flex-direction:column; height:100%; overflow-y:auto; min-height:0; }
    .aiq-header { display:flex; align-items:center; gap:8px; padding:12px 16px; min-height:48px; border-bottom:1px solid var(--border); flex-shrink:0; }
    .aiq-header-title { font-size:16px; font-weight:700; color:var(--fg); flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .aiq-header-actions { display:flex; gap:8px; flex-shrink:0; }
    .aiq-back { background:none; border:none; color:var(--fg-muted); cursor:pointer; padding:4px; min-width:44px; min-height:44px; display:flex; align-items:center; justify-content:center; border-radius:6px; flex-shrink:0; }
    .aiq-back:hover { color:var(--fg); background:var(--surface-2-hover); }

    .aiq-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:12px; padding:16px; }
    @media(max-width:420px) { .aiq-grid { grid-template-columns:repeat(2,1fr); gap:8px; padding:12px; } }
    .aiq-card { display:flex; flex-direction:column; align-items:center; gap:8px; padding:20px 12px; border:1px solid var(--border); background:var(--bg-secondary); border-radius:10px; cursor:pointer; text-align:center; transition:border-color .15s,background .15s; position:relative; }
    .aiq-card:hover { border-color:var(--accent); background:var(--bg-hover); }
    .aiq-card-live { border-color:var(--accent); }
    .aiq-card-live::after { content:''; position:absolute; top:8px; right:8px; width:8px; height:8px; border-radius:50%; background:var(--ok); }
    .aiq-card-icon { width:48px; height:48px; border-radius:33%; background:var(--accent); color:var(--fg-on-accent); display:flex; align-items:center; justify-content:center; font-size:18px; font-weight:700; }
    .aiq-card-icon-add { background:transparent; border:2px dashed var(--border); color:var(--ok); }
    .aiq-card-create { border-style:dashed; }
    .aiq-card-name { font-size:14px; font-weight:600; color:var(--fg); }
    .aiq-card-status { font-size:11px; color:var(--fg-muted); text-transform:uppercase; letter-spacing:.03em; }
    .aiq-card-del { position:absolute; top:6px; right:6px; background:none; border:none; color:var(--fg-muted); cursor:pointer; padding:4px; border-radius:4px; opacity:0; transition:opacity .15s; }
    .aiq-card:hover .aiq-card-del { opacity:1; }
    .aiq-card-del:hover { color:var(--err); background:var(--surface-2-hover); }

    .aiq-threads-list { flex:1; overflow-y:auto; padding:8px 16px; display:flex; flex-direction:column; gap:8px; min-height:0; }
    @media(max-width:420px) { .aiq-threads-list { padding:8px 12px; } }
    .aiq-thread-card { padding:14px 16px; border:1px solid var(--border); border-radius:8px; cursor:pointer; transition:border-color .15s,background .15s; }
    .aiq-thread-card:hover { border-color:var(--accent); background:var(--bg-hover); }
    .aiq-thread-resolved { opacity:.6; }
    .aiq-thread-top { display:flex; align-items:center; gap:8px; justify-content:space-between; }
    .aiq-thread-title { font-size:15px; font-weight:600; color:var(--fg); flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .aiq-thread-badge-open { font-size:11px; padding:2px 8px; border-radius:10px; background:color-mix(in srgb,var(--accent) 15%,transparent); color:var(--accent); font-weight:600; }
    .aiq-thread-badge-resolved { font-size:11px; padding:2px 8px; border-radius:10px; background:color-mix(in srgb,var(--ok) 15%,transparent); color:var(--ok); font-weight:600; }
    .aiq-thread-summary { font-size:12px; color:var(--fg-muted); margin-top:4px; display:-webkit-box; -webkit-line-clamp:1; -webkit-box-orient:vertical; overflow:hidden; }
    .aiq-thread-meta { font-size:11px; color:var(--fg-muted); opacity:.7; margin-top:4px; }

    .aiq-create-thread { padding:12px 16px; padding-bottom:calc(12px + env(safe-area-inset-bottom,0px)); border-top:1px solid var(--border); display:flex; flex-direction:column; gap:8px; flex-shrink:0; }
    @media(max-width:420px) { .aiq-create-thread { padding:12px; } }
    .aiq-thread-input,.aiq-thread-textarea { width:100%; padding:10px 12px; background:var(--bg-input,var(--bg-tertiary)); border:1px solid var(--border); border-radius:6px; color:var(--fg); font:inherit; font-size:14px; }
    .aiq-thread-input:focus,.aiq-thread-textarea:focus { outline:none; border-color:var(--accent); }
    @media(max-width:768px) { .aiq-thread-input,.aiq-thread-textarea { font-size:16px; min-height:44px; } }

    .aiq-tag-row { display:flex; gap:6px; flex-wrap:wrap; padding:8px 16px 0; }
    .aiq-tag { font-size:11px; font-weight:600; padding:2px 8px; border-radius:10px; border:1px solid; }
    .aiq-thread-summary-block { padding:6px 16px; font-size:12px; color:var(--fg-muted); line-height:1.45; border-bottom:1px solid var(--border); }

    .aiq-msgs { flex:1; overflow-y:auto; padding:12px 16px; display:flex; flex-direction:column; gap:8px; min-height:0; }
    @media(max-width:420px) { .aiq-msgs { padding:8px 12px; } }
    .aiq-msg { display:flex; gap:10px; }
    .aiq-msg-avatar { width:32px; height:32px; border-radius:50%; background:var(--accent); color:var(--fg-on-accent); display:flex; align-items:center; justify-content:center; font-size:14px; font-weight:700; flex-shrink:0; }
    .aiq-msg-body { flex:1; min-width:0; }
    .aiq-msg-meta { display:flex; align-items:baseline; gap:6px; }
    .aiq-msg-author { font-size:14px; font-weight:600; color:var(--fg); }
    .aiq-msg-time { font-size:11px; color:var(--fg-muted); }
    .aiq-msg-content { font-size:14px; color:var(--fg-secondary); line-height:1.5; margin-top:2px; white-space:pre-wrap; word-break:break-word; }

    .aiq-compose { display:flex; gap:8px; align-items:flex-end; padding:12px 16px; border-top:1px solid var(--border); flex-shrink:0; }
    @media(max-width:420px) { .aiq-compose { padding:8px 12px; } }
    .aiq-compose-input { flex:1; padding:10px 12px; background:var(--bg-input,var(--bg-tertiary)); border:1px solid var(--border); border-radius:8px; color:var(--fg); font:inherit; font-size:14px; resize:none; min-height:44px; max-height:120px; }
    .aiq-compose-input:focus { outline:none; border-color:var(--accent); }
    @media(max-width:768px) { .aiq-compose-input { font-size:16px; } }
    .aiq-compose-send { width:44px; height:44px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; padding:0; }

    /* Pin the reply + create bars directly above the soft keyboard using the
       app's GLOBAL keyboard system (the same one .chat-compose uses) — never a
       per-view visualViewport/maxHeight hack (that bespoke JS fought
       overlaysContent and is why the gap kept coming back). Two layers, exactly
       mirroring the app: iOS Safari gets --keyboard-offset (app.ts's
       initVisualViewport keeps it in sync); Chromium overrides with the real
       env(keyboard-inset-height) once the VirtualKeyboard API
       (overlaysContent=true, set in app.ts) is active. */
    @media (max-width:768px) {
      .aiq-compose, .aiq-create-thread {
        margin-bottom: var(--keyboard-offset, 0px);
        transition: margin-bottom 120ms ease-out;
      }
    }
    @supports (bottom: env(keyboard-inset-height)) {
      @media (max-width:768px) {
        .aiq-compose, .aiq-create-thread { margin-bottom: env(keyboard-inset-height, 0); }
      }
    }

    .aiq-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border-radius:6px; border:none; font:inherit; font-size:14px; font-weight:500; cursor:pointer; transition:background .15s; min-height:44px; }
    .aiq-btn-primary { background:var(--accent); color:var(--fg-on-accent); }
    .aiq-btn-primary:hover { opacity:.9; }
    .aiq-btn-secondary { background:var(--surface-2); color:var(--fg-muted); }
    .aiq-btn-secondary:hover { background:var(--surface-2-hover); color:var(--fg); }

    .aiq-empty,.aiq-empty-small { padding:24px 16px; color:var(--fg-muted); font-size:14px; text-align:center; }
    .aiq-empty-small { padding:16px; font-size:13px; }

    .aiq-thread-archived-label { font-size:12px; color:var(--fg-muted); padding:6px 12px; background:var(--surface-2); border-radius:6px; }

    .aiq-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.6); z-index:var(--z-modal,300); display:flex; align-items:center; justify-content:center; padding:16px; }
    @media(max-width:540px) {
      .aiq-modal-backdrop {
        align-items:flex-end;
        /* Lift the bottom-sheet modal (New App Idea etc.) above the soft keyboard
           so its title input isn't hidden behind it. A fixed modal can't be
           scrollIntoView'd, so the global keyboard handler can't help — pin it
           with the app's keyboard vars (iOS: --keyboard-offset; Chromium below). */
        padding-bottom: calc(16px + var(--keyboard-offset, 0px));
        transition: padding-bottom 120ms ease-out;
      }
    }
    @supports (bottom: env(keyboard-inset-height)) {
      @media(max-width:540px) {
        .aiq-modal-backdrop { padding-bottom: calc(16px + env(keyboard-inset-height, 0px)); }
      }
    }
    .aiq-modal { background:var(--bg-floating,var(--bg)); border-radius:12px; padding:24px; width:100%; max-width:400px; display:flex; flex-direction:column; gap:16px; }
    @media(max-width:540px) { .aiq-modal { border-radius:12px 12px 0 0; padding-bottom:calc(16px + env(safe-area-inset-bottom,0px)); } }
    .aiq-modal-title { font-size:18px; font-weight:700; color:var(--fg); }
    .aiq-modal-input { width:100%; padding:10px 12px; background:var(--bg-input,var(--bg-tertiary)); border:1px solid var(--border); border-radius:6px; color:var(--fg); font:inherit; font-size:16px; min-height:44px; }
    .aiq-modal-input:focus { outline:none; border-color:var(--accent); }
    .aiq-modal-actions { display:flex; gap:8px; justify-content:flex-end; }
  `;
  document.head.appendChild(style);
}
